class RevealOnScroll {
	constructor(root = document) {
		this.root = root;
		this.breakpoint = 1024;
		this.revealInitialized = false;
		this.breakpointQuery = typeof window !== "undefined" && window.matchMedia ? window.matchMedia(`(min-width: ${this.breakpoint}px)`) : null;
		this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
		this.breakpointListenerBound = false;
		this.init();
	}

	isDesktopViewport() {
		if (this.breakpointQuery) {
			return this.breakpointQuery.matches;
		}
		return (typeof window !== "undefined" ? window.innerWidth : this.breakpoint) >= this.breakpoint;
	}

	bindBreakpointListener() {
		if (!this.breakpointQuery || this.breakpointListenerBound) {
			return;
		}
		const handler = this.handleBreakpointChange;
		if (typeof this.breakpointQuery.addEventListener === "function") {
			this.breakpointQuery.addEventListener("change", handler);
		} else if (typeof this.breakpointQuery.addListener === "function") {
			this.breakpointQuery.addListener(handler);
		}
		this.breakpointListenerBound = true;
	}

	handleBreakpointChange(event) {
		if (event.matches) {
			this.initializeReveals();
		}
	}

	initializeReveals() {
		if (this.revealInitialized) {
			return;
		}
		this.initRevealGrid(this.root, ".js-reveal-grid", ":scope > *");
		this.initRevealTitle(this.root, ".js-reveal-title");
		this.initRevealOnScroll(this.root, ".js-reveal-on-scroll");
		this.revealInitialized = true;
	}

	getColumnCount(gridEl) {
		const style = window.getComputedStyle(gridEl);
		const tpl = style.getPropertyValue("grid-template-columns");
		if (!tpl) return 1;
		return tpl.split(" ").filter(Boolean).length || 1;
	}

	setGridColsClass(gridEl, cols) {
		// remove previous modifier classes
		gridEl.classList.forEach((cls) => {
			if (cls.indexOf("js-reveal-grid--cols-") === 0) {
				gridEl.classList.remove(cls);
			}
		});
		gridEl.classList.add(`js-reveal-grid--cols-${cols}`);
	}

	initRevealGrid(root = this.root, selector = ".js-reveal-grid", itemSelector = ":scope > *") {
		const grids = Array.from(root.querySelectorAll(selector));
		if (!grids.length) return;

		// initial assignment
		grids.forEach((grid) => {
			const cols = this.getColumnCount(grid);
			this.setGridColsClass(grid, cols);
			// ensure items are observed by reveal
			Array.from(grid.querySelectorAll(itemSelector)).forEach((item) => {
				item.classList.add("js-reveal-on-scroll");
			});
		});

		let resizeTimer = null;
		let lastWidth = window.innerWidth || document.documentElement.clientWidth || 0;
		window.addEventListener("resize", () => {
			const currentWidth = window.innerWidth || document.documentElement.clientWidth || 0;
			if (currentWidth === lastWidth) {
				return;
			}
			lastWidth = currentWidth;
			if (!this.isDesktopViewport()) {
				return;
			}
			if (resizeTimer) window.clearTimeout(resizeTimer);
			resizeTimer = window.setTimeout(() => {
				grids.forEach((grid) => {
					const cols = this.getColumnCount(grid);
					this.setGridColsClass(grid, cols);
				});
			}, 150);
		});
	}

	initRevealTitle(root = this.root, selector = ".js-reveal-title", options = {}) {
		const { threshold = 0.15, lineDelayMs = 150, revealDelayMs = 300 } = options;
		const titles = Array.from(root.querySelectorAll(selector));
		if (!titles.length) return;

		// CJK scripts (Japanese kana/kanji, etc.) have no spaces between words and wrap per-character
		const isCJK = (ch) => /[、-〿぀-ヿ㐀-䶿一-鿿豈-﫿＀-￯]/.test(ch);

		const buildVisualLines = (titleEl) => {
			if (!titleEl.dataset.titleOriginalHtml) {
				titleEl.dataset.titleOriginalHtml = titleEl.innerHTML;
			}

			const original = titleEl.dataset.titleOriginalHtml;
			titleEl.innerHTML = "";

			const container = document.createElement("span");
			container.style.display = "inline";
			titleEl.appendChild(container);

			const tempRoot = document.createElement("span");
			tempRoot.style.display = "inline";
			tempRoot.innerHTML = original;
			container.appendChild(tempRoot);

			// On small screens, ignore manual <br> to allow natural wrapping
			const ignoreForcedBreaks = window.matchMedia && window.matchMedia("(max-width: 425px)").matches;
			if (ignoreForcedBreaks) {
				const brs = Array.from(tempRoot.querySelectorAll("br"));
				brs.forEach((br) => br.replaceWith(document.createTextNode(" ")));
			}

			// Collect words using Range to avoid altering layout/kerning
			const words = [];
			const trailingSpaces = [];
			const wordTops = [];
			const walker = document.createTreeWalker(tempRoot, NodeFilter.SHOW_ALL);
			while (walker.nextNode()) {
				const n = walker.currentNode;
				if (n.nodeType === Node.ELEMENT_NODE && n.tagName === "BR") {
					words.push("__BR__");
					trailingSpaces.push("");
					wordTops.push(Number.NaN);
					continue;
				}
				if (n.nodeType === Node.TEXT_NODE) {
					const txt = n.nodeValue || "";
					if (!txt) continue;
					let wordStart = -1;
					const flushWord = (endIdx) => {
						if (wordStart < 0) return;
						const r = document.createRange();
						r.setStart(n, wordStart);
						r.setEnd(n, endIdx);
						const rect = r.getBoundingClientRect();
						words.push(txt.slice(wordStart, endIdx));
						trailingSpaces.push("");
						wordTops.push(Math.round(rect.top));
						wordStart = -1;
					};
					for (let i = 0; i < txt.length; i++) {
						const ch = txt[i];
						if (/\s/.test(ch)) {
							flushWord(i);
							const last = words.length - 1;
							if (last >= 0 && words[last] !== "__BR__") {
								trailingSpaces[last] = " ";
							}
							continue;
						}
						// Each CJK character is its own token so the per-character line breaks are detected
						if (isCJK(ch)) {
							flushWord(i);
							const r = document.createRange();
							r.setStart(n, i);
							r.setEnd(n, i + 1);
							const rect = r.getBoundingClientRect();
							words.push(ch);
							trailingSpaces.push("");
							wordTops.push(Math.round(rect.top));
							continue;
						}
						if (wordStart < 0) wordStart = i;
					}
					flushWord(txt.length);
				}
			}

			const lines = [];
			let currentTop = null;
			let currentLine = [];
			const tolerance = 2;
			for (let i = 0; i < words.length; i++) {
				if (words[i] === "__BR__") {
					if (currentLine.length) {
						lines.push(currentLine);
						currentLine = [];
						currentTop = null;
					}
					continue;
				}
				const top = wordTops[i];
				if (currentTop === null) {
					currentTop = top;
					currentLine.push(i);
					continue;
				}
				if (Math.abs(top - currentTop) <= tolerance) {
					currentLine.push(i);
				} else {
					lines.push(currentLine);
					currentLine = [i];
					currentTop = top;
				}
			}
			if (currentLine.length) lines.push(currentLine);

			// Rebuild with line wrappers while preserving spaces captured after words
			titleEl.innerHTML = "";
			for (let li = 0; li < lines.length; li++) {
				const lineWrapper = document.createElement("span");
				lineWrapper.classList.add("line-wrapper");
				const lineEl = document.createElement("span");
				lineEl.classList.add("line");

				for (let wi = 0; wi < lines[li].length; wi++) {
					const idx = lines[li][wi];
					lineEl.appendChild(document.createTextNode(words[idx]));
					const trailing = trailingSpaces[idx];
					// Normalize any trailing whitespace to a single regular space to avoid stray CR/LF artifacts
					if (trailing && trailing.length) {
						lineEl.appendChild(document.createTextNode(" "));
					}
				}

				lineWrapper.appendChild(lineEl);
				titleEl.appendChild(lineWrapper);
			}

			const lineEls = titleEl.querySelectorAll(".line");
			lineEls.forEach((line, index) => {
				line.style.transitionDelay = `${index * lineDelayMs}ms`;
			});
		};

		Array.from(titles).forEach((el) => {
			if (el.dataset.titleRevealInit === "1") return;
			el.dataset.titleRevealInit = "1";
			buildVisualLines(el);
		});

		let resizeTimer = null;
		let lastWidth = window.innerWidth || document.documentElement.clientWidth || 0;
		window.addEventListener("resize", () => {
			const currentWidth = window.innerWidth || document.documentElement.clientWidth || 0;
			if (currentWidth === lastWidth) {
				return;
			}
			lastWidth = currentWidth;
			if (!this.isDesktopViewport()) {
				return;
			}
			if (resizeTimer) window.clearTimeout(resizeTimer);
			resizeTimer = window.setTimeout(() => {
				titles.forEach((el) => {
					buildVisualLines(el);
				});
			}, 150);
		});

		// Recompute after fonts are loaded to avoid layout changes splitting lines
		if (document.fonts && document.fonts.ready) {
			document.fonts.ready.then(() => {
				if (!this.isDesktopViewport()) {
					return;
				}
				titles.forEach((el) => {
					buildVisualLines(el);
				});
			});
		}

		// Also recompute on window load as a fallback (images, late CSS)
		window.addEventListener("load", () => {
			if (!this.isDesktopViewport()) {
				return;
			}
			titles.forEach((el) => {
				buildVisualLines(el);
			});
		});

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					const el = entry.target;
					if (entry.isIntersecting) {
						if (el._titleRevealTimer) window.clearTimeout(el._titleRevealTimer);
						el._titleRevealTimer = window.setTimeout(() => {
							el.classList.add("is-visible");
							el._titleRevealTimer = null;
						}, revealDelayMs);
					} else {
						if (el._titleRevealTimer) {
							window.clearTimeout(el._titleRevealTimer);
							el._titleRevealTimer = null;
						}
						el.classList.remove("is-visible");
					}
				});
			},
			{ root: null, threshold }
		);

		titles.forEach((el) => observer.observe(el));
	}

	initRevealOnScroll(root = this.root, selector = ".js-reveal-on-scroll", options = {}) {
		const { threshold = 0.15, revealDelayMs = 150 } = options;
		const targets = Array.from(root.querySelectorAll(selector)).filter((el) => !el.classList.contains("js-reveal-title"));
		if (!targets.length) return;

		targets.forEach((el) => {
			if (el.dataset.revealInit === "1") return;
			el.dataset.revealInit = "1";
		});

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					const el = entry.target;
					if (entry.isIntersecting) {
						if (el._genericRevealTimer) window.clearTimeout(el._genericRevealTimer);
						const extraDelay = Number(el.getAttribute("data-reveal-delay") || el.dataset.revealDelay || 0) || 0;
						const totalDelay = Math.max(0, revealDelayMs + extraDelay);
						el._genericRevealTimer = window.setTimeout(() => {
							el.classList.add("is-revealed");
							el._genericRevealTimer = null;
						}, totalDelay);
					} else {
						if (el._genericRevealTimer) {
							window.clearTimeout(el._genericRevealTimer);
							el._genericRevealTimer = null;
						}
						el.classList.remove("is-revealed");
					}
				});
			},
			{ root: null, threshold }
		);

		targets.forEach((el) => observer.observe(el));
	}

	init() {
		// Always initialize if any markers exist on the page
		const markers = document.querySelectorAll(".js-reveal-on-scroll, .js-reveal-grid, .js-reveal-title");
		if (!markers.length) {
			return;
		}

		this.bindBreakpointListener();

		if (!this.isDesktopViewport()) {
			return;
		}

		this.initializeReveals();
	}
}

new RevealOnScroll();
